import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import ToggleInput from '../../elements/ToggleInput';
import NumberInput from '../../elements/NumberInput';
import CurrencyInput from '../../elements/CurrencyInput';
import FormSubmitFooter from '../FormSubmitFooter';
import { z as zod } from 'zod';
import { FieldValues, useForm } from 'react-hook-form';
import {
  Currency,
  EnablePreCheckedNewsletterSubscriptionOption,
  EnableSendRecurringChargeEmailsOption,
  LocationDetails,
} from '../../../types/location';
import { useMutation } from '@tanstack/react-query';
import api from '../../../lib/api';
import {
  ToastHelper,
  ToastMessageType,
  ToastType,
  generateErrorMessageFromResponse,
} from '../../../helper/toast';
import { queryClient } from '../../../query-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { EditLocationContext } from '../../../context/EditLocationContext';
import RadiusInput from '../../elements/RadiusInput';
import {
  editValueGateGeoCheckInByDistance,
  initialValueGateGeoCheckInByDistance,
} from '../../../helper/validation';
import { SelectInputOption } from '../../../types/sharedTypes';
import { useFeatureFlag, User as configCatUser } from 'configcat-react';
import { getXprops } from '../../../hooks/useXProps';
import SearchSelectInput from '../../elements/SearchSelectInput';

interface LocationSettingsFormProps {
  id: string;
  locationDataResponse: LocationDetails;
}

const LocationSettingsForm = ({
  id,
  locationDataResponse,
}: LocationSettingsFormProps) => {
  const { currency } = useContext(EditLocationContext);
  const siteIncluded = locationDataResponse?.included?.find(
    (obj) => obj.type === 'sites',
  );

  const defaultRadiusBySite = siteIncluded?.attributes.geo_check_in_distance;
  const gateGoCheckInByDistanceFromResponse =
    locationDataResponse?.data.attributes?.gate_geo_check_in_by_distance;

  const defaultBuyPageOptions: SelectInputOption[] =
    locationDataResponse?.buy_pages
      .filter((buyPage) => buyPage.attributes.collection_type === 'default')
      .map((buyPage) => {
        return {
          label: buyPage.attributes.name,
          value: buyPage.id,
        };
      });
  const addOnsBuyPageOptions: SelectInputOption[] =
    locationDataResponse?.buy_pages
      .filter((buyPage) => buyPage.attributes.collection_type === 'add_ons')
      .map((buyPage) => {
        return {
          label: buyPage.attributes.name,
          value: buyPage.id,
        };
      });
  const quickSaleBuyPageOptions: SelectInputOption[] =
    locationDataResponse?.buy_pages
      .filter((buyPage) => buyPage.attributes.collection_type === 'quick_sale')
      .map((buyPage) => {
        return {
          label: buyPage.attributes.name,
          value: buyPage.id,
        };
      });

  const initialFormValues = useMemo(
    () =>
      ({
        late_cancel_fee:
          locationDataResponse?.data.attributes?.late_cancel_fee || undefined,
        no_show_fee:
          locationDataResponse?.data.attributes?.no_show_fee || undefined,
        default_product_collection:
          locationDataResponse?.data.relationships.default_product_collection
            ?.data?.id,
        addons_product_collection:
          locationDataResponse?.data.relationships.addons_product_collection
            ?.data?.id,
        quick_sale_product_collection:
          locationDataResponse?.data.relationships.quick_sale_product_collection
            ?.data?.id,
        is_waitlist_position_public:
          locationDataResponse?.data.attributes?.is_waitlist_position_public,
        is_waitlist_count_public:
          locationDataResponse?.data.attributes?.is_waitlist_count_public,
        enable_change_spots:
          locationDataResponse?.data.attributes?.enable_change_spots,
        daily_pending_reservation_limit:
          locationDataResponse?.data.attributes?.daily_pending_reservation_limit?.toString() ||
          undefined,
        class_pass_status:
          locationDataResponse?.data.attributes?.class_pass_status !==
          'disabled',
        enable_third_party_change_spots:
          locationDataResponse?.data.attributes
            ?.enable_third_party_change_spots,
        enable_geo_check_in:
          locationDataResponse?.data.attributes?.enable_geo_check_in,
        gate_geo_check_in_by_distance: initialValueGateGeoCheckInByDistance(
          gateGoCheckInByDistanceFromResponse,
          siteIncluded?.attributes?.gate_geo_check_in_by_distance || false,
        ),
        geo_check_in_distance: locationDataResponse?.data.attributes
          ?.geo_check_in_distance
          ? locationDataResponse?.data.attributes?.geo_check_in_distance?.toString()
          : '0',
        enable_send_recurring_charge_emails:
          locationDataResponse?.data.attributes
            ?.enable_send_recurring_charge_emails ===
          EnableSendRecurringChargeEmailsOption.ALWAYS_ENABLE
            ? true
            : false,
        is_newsletter_subscription_pre_checked:
          locationDataResponse?.data.attributes
            ?.is_newsletter_subscription_pre_checked,
      } as FieldValues),
    [locationDataResponse?.data],
  );

  const Schema = zod.object({
    late_cancel_fee: zod
      .string()
      .optional()
      .nullable()
      .refine((value) => !value || (value.length > 0 && Number(value) >= 0), {
        message: 'Field can not be negative',
      })
      .refine((value) => !value || /^\d+(\.\d{1,2})?$/.test(value), {
        message: 'Ensure that there are no more than 2 decimal places',
      })
      .refine((value) => !value || value?.length < 11, {
        message: 'Ensure that there are no more than 10 digits in total',
      })
      .refine(
        (value) =>
          locationDataResponse?.data.attributes?.late_cancel_fee === '' ||
          value !== '',
        {
          message:
            'A new value of 0 or greater must replace the existing penalty fee',
        },
      ),
    no_show_fee: zod
      .string()
      .optional()
      .nullable()
      .refine((value) => !value || (value.length > 0 && Number(value) >= 0), {
        message: 'Field can not be negative',
      })
      .refine((value) => !value || /^\d+(\.\d{1,2})?$/.test(value), {
        message: 'Ensure that there are no more than 2 decimal places',
      })
      .refine((value) => !value || value?.length < 11, {
        message: 'Ensure that there are no more than 10 digits in total',
      })
      .refine(
        (value) =>
          locationDataResponse?.data.attributes?.no_show_fee === '' ||
          value !== '',
        {
          message:
            'A new value of 0 or greater must replace the existing penalty fee',
        },
      ),
    default_product_collection: zod.string(),
    addons_product_collection: zod.string(),
    quick_sale_product_collection: zod.string(),
    is_waitlist_position_public: zod.boolean(),
    is_waitlist_count_public: zod.boolean(),
    enable_change_spots: zod.boolean(),
    daily_pending_reservation_limit: zod
      .string()
      .optional()
      .nullable()
      .refine((value) => !value || /^[0-9]*$/.test(value), {
        message: 'Field can not be negative or have decimal places',
      }),
    class_pass_status: zod.boolean(),
    enable_third_party_change_spots: zod.boolean(),
    enable_geo_check_in: zod.boolean(),
    gate_geo_check_in_by_distance: zod.boolean(),
    geo_check_in_distance: zod.string().optional().nullable(),
    enable_send_recurring_charge_emails: zod.boolean(),
    is_newsletter_subscription_pre_checked: zod.boolean(),
  });

  type FormSchemaType = zod.infer<typeof Schema>;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<FormSchemaType>({
    defaultValues: initialFormValues,
    resolver: zodResolver(Schema),
    mode: 'all',
  });

  const updateLocationData = useMutation({
    mutationFn: (data: FormSchemaType) =>
      api.fetch('update_location', {
        id: id,
        ...data,
        name: locationDataResponse?.data?.attributes?.name,
        region: locationDataResponse?.included?.[0].id,
        daily_pending_reservation_limit: data.daily_pending_reservation_limit
          ? parseInt(data.daily_pending_reservation_limit, 10)
          : undefined,
        gate_geo_check_in_by_distance: editValueGateGeoCheckInByDistance(
          gateGoCheckInByDistanceFromResponse,
          'gate_geo_check_in_by_distance' in dirtyFields || false,
          data?.gate_geo_check_in_by_distance,
        ),
        geo_check_in_distance:
          data.enable_geo_check_in && data.geo_check_in_distance
            ? parseInt(data.geo_check_in_distance)
            : undefined,
        class_pass_status: !data.class_pass_status
          ? 'disabled'
          : locationDataResponse?.data.attributes?.class_pass_status !==
            'disabled'
          ? locationDataResponse?.data.attributes?.class_pass_status
          : 'request_sent',
        partner_id: locationDataResponse.data.relationships.partner.data.id,
        no_show_fee:
          data.no_show_fee && data.no_show_fee?.length > 0
            ? data.no_show_fee
            : undefined,
        late_cancel_fee:
          data.late_cancel_fee && data.late_cancel_fee?.length > 0
            ? data.late_cancel_fee
            : undefined,
        default_product_collection: data.default_product_collection,
        addons_product_collection: data.addons_product_collection,
        quick_sale_product_collection: data.quick_sale_product_collection,
        legal_entity: locationDataResponse?.data?.attributes?.legal_entity,
        enable_send_recurring_charge_emails:
          data.enable_send_recurring_charge_emails
            ? EnableSendRecurringChargeEmailsOption.ALWAYS_ENABLE
            : EnableSendRecurringChargeEmailsOption.ALWAYS_DISABLE,
        is_newsletter_subscription_pre_checked:
          data.is_newsletter_subscription_pre_checked,
        ...(locationDataResponse?.data.attributes
          ?.is_newsletter_subscription_pre_checked !==
          data.is_newsletter_subscription_pre_checked && {
          enable_pre_checked_newsletter_subscription:
            data.is_newsletter_subscription_pre_checked
              ? EnablePreCheckedNewsletterSubscriptionOption['Opt In']
              : EnablePreCheckedNewsletterSubscriptionOption['Opt Out'],
        }),
      }),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['get_location_by_id'] });
      ToastHelper.showToast(
        'Location',
        ToastType.SUCCESS,
        ToastMessageType.UPDATE_SETTINGS,
      );
    },
    onError: (err: Error) => {
      const errorMessage = generateErrorMessageFromResponse(err);

      ToastHelper.showToast(
        'Location',
        ToastType.ERROR,
        ToastMessageType.ERROR,
        errorMessage,
      );
    },
  });

  const onSubmit = (data: FormSchemaType) => {
    updateLocationData.mutate(data);
  };

  // Get feature flag value(s)
  const tenant = window.sessionStorage.getItem('tenant') || getXprops().tenant;
  const { value: selectBuyPagesInLocationSettings } = useFeatureFlag(
    'selectBuyPagesInLocationSettings',
    false,
    new configCatUser('', undefined, undefined, { subdomain: tenant }),
  );

  return (
    <div>
      <div className='w-full mt-5 p-6 pb-6 bg-white  border border-gray-light rounded-lg'>
        <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left'>
          Automatic Fees
        </h1>
        <div className='mt-3 mb-6'>
          <HighlightBox>
            Once updated, these fees will automatically apply to upcoming
            reservations without needing support assistance.
          </HighlightBox>
        </div>
        <InputGrid>
          <CurrencyInput
            id='late_cancel_fee'
            label='Late Cancel Fee'
            currency={currency ?? Currency['United States dollar']}
            placeholder='10.00'
            description='The fee when a customer ‘late cancels’ while reserving with a membership'
            disabled={false}
            {...register('late_cancel_fee')}
            error={errors['late_cancel_fee']?.message}
          />
          <CurrencyInput
            id='no_show_fee'
            label='No Show Fee'
            currency={currency ?? Currency['United States dollar']}
            placeholder='10.00'
            description='The fee when a customer does not show up for class while reserving with a membership'
            disabled={false}
            {...register('no_show_fee')}
            error={errors['no_show_fee']?.message}
          />
        </InputGrid>
      </div>
      {selectBuyPagesInLocationSettings && (
        <div className='w-full mt-2 p-6 pb-2 bg-white  border border-gray-light rounded-lg'>
          <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left pb-6'>
            Buy Page Settings
          </h1>
          <InputGrid>
            <SearchSelectInput
              label='Default Buy Page'
              description='Main buy page customers use on web integrations and the iOS and Android apps.'
              options={defaultBuyPageOptions}
              value={watch('default_product_collection')}
              {...register('default_product_collection')}
            />
          </InputGrid>
          <InputGrid>
            <SearchSelectInput
              label='Add Ons Buy Page'
              description='Buy page customers see after booking a reservation.'
              options={addOnsBuyPageOptions}
              value={watch('addons_product_collection')}
              {...register('addons_product_collection')}
            />
          </InputGrid>
          <InputGrid>
            <SearchSelectInput
              label='Quick Sale Buy Page'
              description='List of products available for sale in the roster.'
              options={quickSaleBuyPageOptions}
              value={watch('quick_sale_product_collection')}
              {...register('quick_sale_product_collection')}
            />
          </InputGrid>
        </div>
      )}
      <div className='w-full mt-2 p-6 pb-2 bg-white  border border-gray-light rounded-lg'>
        <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left pb-6'>
          Reservation Settings
        </h1>
        <InputGrid>
          <ToggleInput
            id='is_waitlist_position_public'
            label='Waitlist Position'
            description='Allow customers to see their waitlist position on app or website'
            {...register('is_waitlist_position_public')}
            checked={watch('is_waitlist_position_public')}
          />
          <ToggleInput
            id='is_waitlist_count_public'
            label='Waitlist Count'
            description='Allow customers to see the waitlist count'
            {...register('is_waitlist_count_public')}
            checked={watch('is_waitlist_count_public')}
          />
        </InputGrid>
        <InputGrid>
          <ToggleInput
            id='enable_change_spots'
            label='Change Spots'
            description='Allow customers to change spots via app or website'
            {...register('enable_change_spots')}
            checked={watch('enable_change_spots')}
          />
          <NumberInput
            id='daily_reservation_limit'
            label='Daily Reservation Limit'
            placeholder='0'
            disabled={false}
            description='Number of pending reservations a customer can book in the same day'
            {...register('daily_pending_reservation_limit')}
            error={errors['daily_pending_reservation_limit']?.message}
          />
        </InputGrid>
      </div>
      <div className='w-full mt-2 p-6 pb-2 bg-white  border border-gray-light rounded-lg'>
        <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left pb-6'>
          Third Party Booking
        </h1>
        <InputGrid>
          <ToggleInput
            id='class_pass_status'
            label='ClassPass'
            description='Enable ClassPass integration for this location'
            {...register('class_pass_status')}
            checked={watch('class_pass_status')}
          />
          <ToggleInput
            id='enable_third_party_change_spots'
            label='Third Party Change Spots'
            description='Allow third party customers to change spots via app or website'
            {...register('enable_third_party_change_spots')}
            checked={watch('enable_third_party_change_spots')}
          />
        </InputGrid>
      </div>
      <div className='w-full mt-2 p-6 pb-3 bg-white  border border-gray-light rounded-lg'>
        <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left pb-6'>
          Mobile Check-In Settings
        </h1>
        <InputGrid>
          <ToggleInput
            id='enable_geo_check_in'
            label='Mobile Check-In'
            description='Allow customers to self check-in via your iOS or android app.'
            {...register('enable_geo_check_in')}
            checked={watch('enable_geo_check_in')}
          />
        </InputGrid>
        {watch('enable_geo_check_in') && (
          <InputGrid>
            <ToggleInput
              id='gate_geo_check_in_by_distance'
              label='Geofence'
              description='Allow customers to self check-in via your iOS  or android app when they come within a certain distance of your studios.'
              {...register('gate_geo_check_in_by_distance')}
              checked={watch('gate_geo_check_in_by_distance')}
            />
          </InputGrid>
        )}
        {watch('enable_geo_check_in') &&
          watch('gate_geo_check_in_by_distance') && (
            <InputGrid>
              <RadiusInput
                id='check_in_radius'
                label='Check-in radius'
                placeholder='0'
                description='A location-level override of your default mobile check-in radius (in meters).'
                {...register('geo_check_in_distance')}
                error={errors['geo_check_in_distance']?.message}
                datatestid='check_in_radius'
                defaultradius={defaultRadiusBySite}
              />
            </InputGrid>
          )}
      </div>

      <div className='w-full mt-2 p-6 pb-3 bg-white  border border-gray-light rounded-lg'>
        <h1 className='font-display text-xl font-semibold leading-7 tracking-normal text-left pb-6'>
          Communication Settings
        </h1>
        <InputGrid>
          <ToggleInput
            id='enable_send_recurring_charge_emails'
            label='Membership Renewal Receipt Email'
            description='Enable email receipts for membership renewal payments.'
            {...register('enable_send_recurring_charge_emails')}
            checked={watch('enable_send_recurring_charge_emails')}
          />

          <ToggleInput
            id='is_newsletter_subscription_pre_checked'
            label='Default Marketing Email Opt-In'
            description='Check the marketing email opt-in checkbox by default on account creation.'
            {...register('is_newsletter_subscription_pre_checked')}
            checked={watch('is_newsletter_subscription_pre_checked')}
          />
        </InputGrid>
      </div>
      <FormSubmitFooter
        handleSubmit={handleSubmit(onSubmit)}
        loading={updateLocationData.isPending}
        disabled={Object.keys(dirtyFields).length === 0}
      />
    </div>
  );
};

const InputGrid = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  align-items: center;
`;

const HighlightBox = styled.div`
  background-color: #ffffe0;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  line-height: 1.4;
  font-weight: normal;
`;

export default LocationSettingsForm;
